import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useStore } from '@/store'
import type { ActiveFilters, Dimension, HexMode } from '@/types'
import {
  CATALOG_BRANDS,
  CATALOG_GAMES,
  CATALOG_STUDIOS,
  CATALOG_STREAMS,
} from '@/data/catalog'
import cdnEdgesData from '@/data/catalog/cdnEdges.json'
import { CONTINENT_NAMES, REGION_LABEL } from '@/constants'
import type { SearchSliceNode, StreamSliceNode } from '@/analysis/searchContract'
import type { WorldFlowHop } from '@/analysis/worldFlowTypes'

type FilterKey = Exclude<keyof ActiveFilters, 'severity' | 'userId' | 'region' | 'streamKey'>
type ChipKey = FilterKey
type CdnEdge = { label: string }
type ClientGrain = 'continent' | 'country' | 'city'

const CONTINENT_CODES = new Set(['EU', 'NA', 'SA', 'AS', 'AF', 'OC', 'OT', 'AN', 'ME'])
const CONTINENT_NAME_LC = new Set(CONTINENT_NAMES.map((c) => c.toLowerCase()))

const HEX_MODES: { value: HexMode; label: string }[] = [
  { value: 'stream', label: 'Stream' },
  { value: 'studio', label: 'Studio' },
  { value: 'game', label: 'Game' },
]

function uniqSorted(xs: Array<string | undefined | null>) {
  return Array.from(new Set(xs.map((x) => String(x ?? '').trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b),
  )
}

function matchesSearch(s: string, q: string) {
  if (!q) return true
  return s.toLowerCase().includes(q.toLowerCase())
}

/** Map continent codes / aliases to display names using REGION_LABEL and CONTINENT_NAMES. */
function normalizeContinent(raw: string | null | undefined): string | null {
  const s = String(raw ?? '').trim()
  if (!s) return null

  if (CONTINENT_NAMES.includes(s)) return s

  const upper = s.toUpperCase()
  if (CONTINENT_CODES.has(upper) || REGION_LABEL[upper]) {
    const label = REGION_LABEL[upper]
    if (label && CONTINENT_NAMES.includes(label)) return label
    if (label && CONTINENT_NAME_LC.has(label.toLowerCase())) return label
  }

  const ci = CONTINENT_NAMES.find((c) => c.toLowerCase() === s.toLowerCase())
  return ci ?? null
}

type ClientGeoExtract = {
  continents: string[]
  countries: string[]
  citiesByCountry: Record<string, string[]>
  cityCountryMap: Record<string, string>
}

/** Grain-aware client geo options from world-flow city hops. */
function extractClientGeo(hops: WorldFlowHop[], worldClientGrain: ClientGrain): ClientGeoExtract {
  const clientHops = hops.filter((h) => h.type === 'city')
  const continentSet = new Set<string>()
  const countrySet = new Set<string>()
  const citiesByCountry: Record<string, string[]> = {}
  const cityCountryMap: Record<string, string> = {}

  // Always collect continents so Advanced Filters stays populated at any grain.
  for (const h of clientHops) {
    const name = normalizeContinent(h.continent ?? (worldClientGrain === 'continent' ? h.value : null))
    if (name) continentSet.add(name)
  }

  if (worldClientGrain === 'continent') {
    for (const h of clientHops) {
      const name = normalizeContinent(h.continent ?? h.value)
      if (name) continentSet.add(name)
    }
  } else if (worldClientGrain === 'country') {
    for (const h of clientHops) {
      const cc = String(h.country ?? h.value ?? '')
        .trim()
        .toUpperCase()
      if (cc.length === 2) countrySet.add(cc)
    }
  } else {
    for (const h of clientHops) {
      const raw = String(h.value ?? '').trim()
      if (!raw) continue

      let city: string | null = null
      let country = String(h.country ?? '').trim().toUpperCase()

      if (raw.includes('|')) {
        const [cityPart, ccPart] = raw.split('|')
        city = cityPart?.trim() ?? null
        if (ccPart?.trim()) country = ccPart.trim().toUpperCase()
      } else if (raw.length === 2 && /^[A-Z]{2}$/.test(raw.toUpperCase())) {
        continue
      } else {
        city = raw
      }

      if (!city) continue
      if (CONTINENT_NAME_LC.has(city.toLowerCase())) continue
      if (normalizeContinent(city)) continue

      const cc = country || '—'
      if (!citiesByCountry[cc]) citiesByCountry[cc] = []
      if (!citiesByCountry[cc].includes(city)) citiesByCountry[cc].push(city)
      if (country) cityCountryMap[city] = country
    }

    for (const cc of Object.keys(citiesByCountry)) {
      citiesByCountry[cc].sort((a, b) => a.localeCompare(b))
    }
  }

  // Countries/cities also useful when grain is continent (from hop metadata).
  if (worldClientGrain === 'continent') {
    for (const h of clientHops) {
      const cc = String(h.country ?? '').trim().toUpperCase()
      if (cc.length === 2) countrySet.add(cc)
    }
  }

  return {
    continents: [...continentSet].sort((a, b) => a.localeCompare(b)),
    countries: [...countrySet].sort((a, b) => a.localeCompare(b)),
    citiesByCountry,
    cityCountryMap,
  }
}

function collectStreamNodes(
  searchResult: { nodes?: SearchSliceNode[] } | null,
  searchResultDrill: { nodes?: SearchSliceNode[] } | null,
): StreamSliceNode[] {
  const out: StreamSliceNode[] = []
  for (const env of [searchResult, searchResultDrill]) {
    if (!env?.nodes) continue
    for (const n of env.nodes) {
      if (n.kind === 'stream') out.push(n as StreamSliceNode)
    }
  }
  return out
}

function mergeCitiesByCountry(
  a: Record<string, string[]>,
  b: Record<string, string[]>,
): Record<string, string[]> {
  const out: Record<string, string[]> = { ...a }
  for (const [cc, cities] of Object.entries(b)) {
    out[cc] = uniqSorted([...(out[cc] ?? []), ...cities])
  }
  return out
}

function labelForKey(k: ChipKey) {
  if (k === 'continent') return 'Continent'
  if (k === 'country') return 'Country'
  if (k === 'city') return 'City'
  if (k === 'cdnEdge') return 'CDN edge'
  if (k === 'studio') return 'Studio'
  if (k === 'game') return 'Game'
  if (k === 'brand') return 'Brand'
  if (k === 'sourceUrl') return 'Source URL'
  if (k === 'streamType') return 'Stream type'
  if (k === 'deviceType') return 'Device type'
  if (k === 'platform') return 'Platform'
  if (k === 'browser') return 'Browser'
  if (k === 'os') return 'OS'
  return k
}

function useFixedPanelPosition(open: boolean, triggerRef: React.RefObject<HTMLElement | null>) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 240 })

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) })
  }, [open, triggerRef])

  return pos
}

function useCloseOnOutsideAndScroll(
  open: boolean,
  onClose: () => void,
  triggerRef: React.RefObject<HTMLElement | null>,
  panelRef: React.RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return

    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return
      onClose()
    }

    const onScroll = () => onClose()

    document.addEventListener('mousedown', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open, onClose, triggerRef, panelRef])
}

const panelShellStyle: React.CSSProperties = {
  background: 'var(--bg-2)',
  border: '0.5px solid var(--bd-1)',
  borderRadius: 'var(--radius-lg)',
  padding: 10,
  boxShadow: '0 10px 34px rgba(0,0,0,0.55)',
}

function FilterDropdown({
  title,
  selected,
  options,
  onToggle,
  onClear,
  placeholder = 'Search…',
  emptyLabel = 'No data yet',
}: {
  title: string
  selected: string[]
  options: string[]
  onToggle: (v: string) => void
  onClear: () => void
  placeholder?: string
  emptyLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const pos = useFixedPanelPosition(open, triggerRef)

  useCloseOnOutsideAndScroll(open, () => setOpen(false), triggerRef, panelRef)

  const shown = options.filter((o) => matchesSearch(o, q))

  return (
    <div style={{ minWidth: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          height: 28,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '0 10px',
          borderRadius: 'var(--radius)',
          border: '0.5px solid var(--bd-1)',
          background: 'var(--bg-3)',
          color: 'var(--t-0)',
          cursor: 'pointer',
          fontSize: 11,
          fontFamily: 'var(--font)',
        }}
      >
        <span style={{ color: 'var(--t-2)', fontSize: 10, fontWeight: 600, letterSpacing: '.06em' }}>
          {title.toUpperCase()}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            color: selected.length ? 'var(--t-0)' : 'var(--t-2)',
            fontSize: 11,
            fontWeight: 500,
          }}
        >
          {selected.length ? `${selected.length} selected` : 'All'}
        </span>
        <span style={{ color: 'var(--t-2)' }}>{open ? '▴' : '▾'}</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              ...panelShellStyle,
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 30000,
            }}
          >
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              style={{
                width: '100%',
                height: 26,
                fontSize: 11,
                padding: '0 10px',
                borderRadius: 20,
                border: '0.5px solid var(--bd-1)',
                background: 'var(--bg-3)',
                color: 'var(--t-0)',
                outline: 'none',
                fontFamily: 'var(--font)',
                marginBottom: 8,
              }}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 220, overflow: 'auto', paddingRight: 2 }}>
              {!options.length ? (
                <span style={{ fontSize: 10, color: 'var(--t-2)' }}>{emptyLabel}</span>
              ) : shown.length ? (
                shown.map((opt) => {
                  const on = selected.includes(opt)
                  return (
                    <span
                      key={opt}
                      style={{
                        fontSize: 10,
                        padding: '2px 8px',
                        borderRadius: 20,
                        cursor: 'pointer',
                        border: `0.5px solid ${on ? 'var(--info-bd)' : 'var(--bd-1)'}`,
                        background: on ? 'var(--info-bg)' : 'transparent',
                        color: on ? 'var(--info)' : 'var(--t-1)',
                        userSelect: 'none',
                      }}
                      onClick={() => onToggle(opt)}
                    >
                      {opt}
                    </span>
                  )
                })
              ) : (
                <span style={{ fontSize: 10, color: 'var(--t-2)' }}>No matches</span>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
              <span
                className="chip"
                onClick={() => {
                  onClear()
                  setQ('')
                }}
              >
                Clear
              </span>
              <span
                className="chip on"
                style={{ background: 'var(--info-bg)', color: 'var(--info)', borderColor: 'var(--info-bd)' }}
                onClick={() => setOpen(false)}
              >
                Done
              </span>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

function CityGroupedDropdown({
  title,
  selected,
  citiesByCountry,
  cityCountryMap,
  onToggleCity,
  onClear,
}: {
  title: string
  selected: string[]
  citiesByCountry: Record<string, string[]>
  cityCountryMap: Record<string, string>
  onToggleCity: (city: string, countryHint?: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const pos = useFixedPanelPosition(open, triggerRef)

  useCloseOnOutsideAndScroll(open, () => setOpen(false), triggerRef, panelRef)

  const countryKeys = Object.keys(citiesByCountry).sort((a, b) => a.localeCompare(b))
  const totalCities = countryKeys.reduce((n, cc) => n + citiesByCountry[cc].length, 0)

  return (
    <div style={{ minWidth: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          height: 28,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '0 10px',
          borderRadius: 'var(--radius)',
          border: '0.5px solid var(--bd-1)',
          background: 'var(--bg-3)',
          color: 'var(--t-0)',
          cursor: 'pointer',
          fontSize: 11,
          fontFamily: 'var(--font)',
        }}
      >
        <span style={{ color: 'var(--t-2)', fontSize: 10, fontWeight: 600, letterSpacing: '.06em' }}>
          {title.toUpperCase()}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            color: selected.length ? 'var(--t-0)' : 'var(--t-2)',
            fontSize: 11,
            fontWeight: 500,
          }}
        >
          {selected.length ? `${selected.length} selected` : 'All'}
        </span>
        <span style={{ color: 'var(--t-2)' }}>{open ? '▴' : '▾'}</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              ...panelShellStyle,
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: Math.max(pos.width, 280),
              zIndex: 30000,
              maxHeight: 'min(60vh, 420px)',
              overflow: 'auto',
            }}
          >
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search cities…"
              style={{
                width: '100%',
                height: 26,
                fontSize: 11,
                padding: '0 10px',
                borderRadius: 20,
                border: '0.5px solid var(--bd-1)',
                background: 'var(--bg-3)',
                color: 'var(--t-0)',
                outline: 'none',
                fontFamily: 'var(--font)',
                marginBottom: 8,
              }}
            />

            {!totalCities ? (
              <span style={{ fontSize: 10, color: 'var(--t-2)' }}>No data yet</span>
            ) : (
              countryKeys.map((cc) => {
                const cities = citiesByCountry[cc].filter((city) => matchesSearch(`${city} ${cc}`, q))
                if (!cities.length) return null
                return (
                  <div key={cc} style={{ marginBottom: 10 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--t-2)',
                        letterSpacing: '.05em',
                        marginBottom: 6,
                      }}
                    >
                      {cc}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {cities.map((city) => {
                        const on = selected.includes(city)
                        return (
                          <span
                            key={`${cc}:${city}`}
                            style={{
                              fontSize: 10,
                              padding: '2px 8px',
                              borderRadius: 20,
                              cursor: 'pointer',
                              border: `0.5px solid ${on ? 'var(--info-bd)' : 'var(--bd-1)'}`,
                              background: on ? 'var(--info-bg)' : 'transparent',
                              color: on ? 'var(--info)' : 'var(--t-1)',
                              userSelect: 'none',
                            }}
                            onClick={() => onToggleCity(city, cityCountryMap[city] ?? cc)}
                          >
                            {city}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )
              })
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
              <span
                className="chip"
                onClick={() => {
                  onClear()
                  setQ('')
                }}
              >
                Clear
              </span>
              <span
                className="chip on"
                style={{ background: 'var(--info-bg)', color: 'var(--info)', borderColor: 'var(--info-bd)' }}
                onClick={() => setOpen(false)}
              >
                Done
              </span>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

function SourceUrlAutocomplete({
  title,
  selected,
  options,
  onToggle,
  onClear,
}: {
  title: string
  selected: string[]
  options: string[]
  onToggle: (v: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const pos = useFixedPanelPosition(open, triggerRef)

  useCloseOnOutsideAndScroll(open, () => setOpen(false), triggerRef, panelRef)

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  const shown = options.filter((o) => matchesSearch(o, q)).slice(0, 40)

  const commit = (raw: string) => {
    const v = raw.trim()
    if (!v) return
    onToggle(v)
    setQ('')
    setOpen(false)
  }

  return (
    <div style={{ minWidth: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          height: 28,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '0 10px',
          borderRadius: 'var(--radius)',
          border: '0.5px solid var(--bd-1)',
          background: 'var(--bg-3)',
          color: 'var(--t-0)',
          cursor: 'pointer',
          fontSize: 11,
          fontFamily: 'var(--font)',
        }}
      >
        <span style={{ color: 'var(--t-2)', fontSize: 10, fontWeight: 600, letterSpacing: '.06em' }}>
          {title.toUpperCase()}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            color: selected.length ? 'var(--t-0)' : 'var(--t-2)',
            fontSize: 11,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 140,
          }}
        >
          {selected.length ? `${selected.length} selected` : 'All'}
        </span>
        <span style={{ color: 'var(--t-2)' }}>{open ? '▴' : '▾'}</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              ...panelShellStyle,
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: Math.max(pos.width, 320),
              zIndex: 30000,
            }}
          >
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit(q)
                  if (e.key === 'Escape') setOpen(false)
                }}
                placeholder="URL or stream key…"
                style={{
                  flex: 1,
                  height: 26,
                  fontSize: 11,
                  padding: '0 10px',
                  borderRadius: 20,
                  border: '0.5px solid var(--bd-1)',
                  background: 'var(--bg-3)',
                  color: 'var(--t-0)',
                  outline: 'none',
                  fontFamily: 'var(--font)',
                }}
              />
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={onClear}
                  style={{
                    height: 26,
                    padding: '0 10px',
                    borderRadius: 20,
                    border: '0.5px solid var(--bd-1)',
                    background: 'transparent',
                    color: 'var(--t-2)',
                    fontSize: 10,
                    cursor: 'pointer',
                    fontFamily: 'var(--font)',
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            {!options.length ? (
              <div style={{ fontSize: 10, color: 'var(--t-2)', padding: '6px 8px' }}>No data yet</div>
            ) : shown.length ? (
              shown.map((opt) => (
                <div
                  key={opt}
                  style={{
                    fontSize: 11,
                    padding: '6px 8px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    color: selected.includes(opt) ? 'var(--info)' : 'var(--t-0)',
                    background: selected.includes(opt) ? 'var(--info-bg)' : 'transparent',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={opt}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(opt)}
                >
                  {opt}
                </div>
              ))
            ) : q.trim() ? (
              <div
                style={{ fontSize: 11, padding: '6px 8px', cursor: 'pointer', color: 'var(--t-1)' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(q)}
              >
                Use “{q.trim()}”
              </div>
            ) : (
              <div style={{ fontSize: 10, color: 'var(--t-2)', padding: '6px 8px' }}>Type to search…</div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}

function sectionLabel(text: string) {
  return (
    <div
      style={{
        gridColumn: '1 / -1',
        fontSize: 10,
        fontWeight: 500,
        color: 'var(--t-2)',
        textTransform: 'uppercase',
        letterSpacing: '.07em',
        marginTop: 2,
      }}
    >
      {text}
    </div>
  )
}

function asHexMode(d: Dimension): HexMode {
  if (d === 'studio' || d === 'game' || d === 'stream') return d
  return 'stream'
}

export default function FilterBar() {
  const filters = useStore((s) => s.filters)
  const toggleFilterValue = useStore((s) => s.toggleFilterValue)
  const setFilterOnly = useStore((s) => s.setFilterOnly)
  const clearFilter = useStore((s) => s.clearFilter)
  const clearFilters = useStore((s) => s.clearFilters)
  const toggleSeverity = useStore((s) => s.toggleSeverity)
  const dimension = useStore((s) => s.dimension)
  const setDimension = useStore((s) => s.setDimension)
  const searchResult = useStore((s) => s.searchResult)
  const searchResultDrill = useStore((s) => s.searchResultDrill)
  const worldFlowResult = useStore((s) => s.worldFlowResult)
  const worldClientGrain = useStore((s) => s.worldClientGrain)
  const view = useStore((s) => s.view)
  const overlayPanel = useStore((s) => s.overlayPanel)
  const setOverlayPanel = useStore((s) => s.setOverlayPanel)
  const timeMode = useStore((s) => s.timeMode)
  const timeWindow = useStore((s) => s.window)
  const historicFromTs = useStore((s) => s.historicFromTs)
  const historicTs = useStore((s) => s.historicTs)
  const setTimeMode = useStore((s) => s.setTimeMode)

  const popOpen = overlayPanel === 'filters'
  const setPopOpen = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved = typeof next === 'function' ? next(popOpen) : next
    setOverlayPanel(resolved ? 'filters' : null)
  }
  const timeFilterActive =
    timeMode === 'historic' &&
    timeWindow === 'custom' &&
    historicFromTs != null &&
    historicTs != null

  const mode = asHexMode(dimension)
  const hops = worldFlowResult?.hops ?? []

  const streamNodes = useMemo(
    () => collectStreamNodes(searchResult, searchResultDrill),
    [searchResult, searchResultDrill],
  )

  const hopGeo = useMemo(
    () => extractClientGeo(hops, worldClientGrain),
    [hops, worldClientGrain],
  )

  const streamGeo = useMemo(() => {
    const continents = new Set<string>()
    const countries = new Set<string>()
    const citiesByCountry: Record<string, string[]> = {}
    const cityCountryMap: Record<string, string> = {}

    for (const n of streamNodes) {
      const con = normalizeContinent(n.continent)
      if (con) continents.add(con)

      const cc = n.country?.trim().toUpperCase()
      if (cc) countries.add(cc)

      const city = n.city?.trim()
      if (city && cc) {
        if (!citiesByCountry[cc]) citiesByCountry[cc] = []
        if (!citiesByCountry[cc].includes(city)) citiesByCountry[cc].push(city)
        cityCountryMap[city] = cc
      }
    }

    for (const cc of Object.keys(citiesByCountry)) {
      citiesByCountry[cc].sort((a, b) => a.localeCompare(b))
    }

    return { continents: [...continents], countries: [...countries], citiesByCountry, cityCountryMap }
  }, [streamNodes])

  const geoOptions = useMemo(() => {
    const continents = uniqSorted([
      ...hopGeo.continents,
      ...streamGeo.continents,
      // Always offer canonical continent names so Advanced Filters is never empty.
      ...CONTINENT_NAMES,
    ])
    const countries = uniqSorted([...hopGeo.countries, ...streamGeo.countries])
    const citiesByCountry = mergeCitiesByCountry(hopGeo.citiesByCountry, streamGeo.citiesByCountry)
    const cityCountryMap = { ...streamGeo.cityCountryMap, ...hopGeo.cityCountryMap }
    return { continents, countries, citiesByCountry, cityCountryMap }
  }, [hopGeo, streamGeo])

  const identityOptions = useMemo(
    () => ({
      studios: uniqSorted([
        ...CATALOG_STUDIOS.map((s) => s.code),
        ...streamNodes.map((n) => n.studio),
        ...hops.filter((h) => h.type === 'studio').map((h) => h.value),
      ]),
      games: uniqSorted([
        ...CATALOG_GAMES.map((g) => g.code),
        ...streamNodes.map((n) => (n as StreamSliceNode & { game?: string }).game),
      ]),
      brands: uniqSorted(CATALOG_BRANDS.map((b) => b.code)),
    }),
    [streamNodes, hops],
  )

  const deliveryOptions = useMemo(
    () => ({
      cdnEdge: uniqSorted([
        ...(cdnEdgesData as CdnEdge[]).map((e) => e.label),
        ...streamNodes.map((n) => n.cdnEdge),
        ...hops.filter((h) => h.type === 'cdn_edge').map((h) => h.value),
      ]),
    }),
    [streamNodes, hops],
  )

  const sourceUrlOptions = useMemo(
    () =>
      uniqSorted([
        ...streamNodes.map((n) => n.sourceUrl),
        ...streamNodes.map((n) => n.streamId || n.value),
        ...CATALOG_STREAMS.flatMap((row) => [row.url, row.streamKey]),
      ]),
    [streamNodes],
  )

  const streamTypeOptions = useMemo(
    () =>
      uniqSorted([
        ...streamNodes.map((n) => n.streamType),
        ...CATALOG_STREAMS.map((row) => row.type),
        'millicast',
        'flv',
      ]),
    [streamNodes],
  )

  const clientOptions = useMemo(
    () => ({
      deviceType: uniqSorted(streamNodes.map((n) => n.deviceType)),
      platform: uniqSorted(streamNodes.map((n) => n.platform ?? n.os)),
      browser: uniqSorted(streamNodes.map((n) => n.browser)),
    }),
    [streamNodes],
  )

  const chipKeys = (Object.keys(filters) as Array<keyof ActiveFilters>).filter(
    (k): k is ChipKey => k !== 'severity' && k !== 'userId' && k !== 'region' && k !== 'streamKey',
  )

  const activeDimFilters = chipKeys
    .map((k) => ({ key: k, title: labelForKey(k), selected: (filters[k] as string[]) ?? [] }))
    .filter((g) => g.selected.length > 0)

  const hasActive = activeDimFilters.length > 0 || timeFilterActive
  const showModeSelector = view === 'hex' || view === 'deep'

  const toggleCity = (city: string, countryHint?: string) => {
    setFilterOnly('city', city)
    const cc = countryHint?.trim().toUpperCase()
    if (cc && cc !== '—') {
      setFilterOnly('country', cc)
    }
  }

  return (
    <div
      style={{
        background: 'var(--bg-1)',
        borderBottom: '0.5px solid var(--bd-0)',
        backdropFilter: 'blur(12px)',
        position: 'relative',
        flexShrink: 0,
        zIndex: 2000,
      }}
    >
      <div style={{ padding: '7px 16px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {showModeSelector && (
          <>
            <span style={{ fontSize: 10, color: 'var(--t-2)', fontWeight: 500, marginRight: 2 }}>Mode</span>
            <select
              value={mode}
              onChange={(e) => setDimension(e.target.value as HexMode)}
              style={{
                height: 24,
                fontSize: 11,
                padding: '0 8px',
                borderRadius: 20,
                border: '0.5px solid var(--bd-1)',
                background: 'var(--bg-3)',
                color: 'var(--t-0)',
                fontFamily: 'var(--font)',
                cursor: 'pointer',
              }}
            >
              {HEX_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <div className="vd" />
          </>
        )}

        {activeDimFilters.map((g) => (
          <span
            key={g.key}
            style={{
              fontSize: 10,
              padding: '2px 8px',
              borderRadius: 20,
              background: 'var(--info-bg)',
              color: 'var(--info)',
              border: '0.5px solid var(--info-bd)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
            onClick={() => clearFilter(g.key)}
          >
            {g.selected.length === 1
              ? `${g.title}: ${g.selected[0]} ✕`
              : `${g.title} (${g.selected.length}) ✕`}
          </span>
        ))}

        {timeFilterActive && historicFromTs != null && historicTs != null && (
          <span
            title="AcePlayer tick window filter — click to clear"
            style={{
              fontSize: 10,
              padding: '2px 8px',
              borderRadius: 20,
              background: 'var(--info-bg)',
              color: 'var(--info)',
              border: '0.5px solid var(--info-bd)',
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
            onClick={() => setTimeMode('live')}
          >
            Tick window · {new Date(historicFromTs).toLocaleTimeString('en-GB')}–
            {new Date(historicTs).toLocaleTimeString('en-GB')} ✕
          </span>
        )}

        <button
          onClick={() => setPopOpen((o) => !o)}
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            padding: '3px 9px',
            borderRadius: 20,
            border: `0.5px solid ${popOpen ? 'var(--info-bd)' : 'var(--bd-1)'}`,
            background: popOpen ? 'var(--info-bg)' : 'transparent',
            color: popOpen ? 'var(--info)' : 'var(--t-2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontFamily: 'var(--font)',
          }}
        >
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="8" y1="2" x2="8" y2="14" />
            <line x1="2" y1="8" x2="14" y2="8" />
          </svg>
          {hasActive ? `Filters (${activeDimFilters.length})` : 'Advanced filters'}
        </button>

        {hasActive && (
          <span style={{ fontSize: 10, color: 'var(--t-2)', cursor: 'pointer' }} onClick={clearFilters}>
            Clear all
          </span>
        )}
      </div>

      {popOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 16,
            zIndex: 10000,
            background: 'var(--bg-2)',
            border: '0.5px solid var(--bd-1)',
            borderRadius: 'var(--radius-lg)',
            padding: 16,
            width: 760,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 14,
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            overflow: 'visible',
          }}
        >
          <div style={{ gridColumn: '1 / -1' }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: 'var(--t-2)',
                textTransform: 'uppercase',
                letterSpacing: '.07em',
                marginBottom: 6,
              }}
            >
              Severity
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(
                [
                  { key: 0, label: 'Critical', col: 'var(--crit)', bg: 'var(--crit-bg)', bd: 'var(--crit-bd)' },
                  { key: 1, label: 'Warning', col: 'var(--warn)', bg: 'var(--warn-bg)', bd: 'var(--warn-bd)' },
                  { key: 2, label: 'OK', col: 'var(--ok)', bg: 'var(--ok-bg)', bd: 'var(--ok-bd)' },
                  { key: null, label: 'No data', col: 'var(--t-2)', bg: 'var(--bg-3)', bd: 'var(--bd-1)' },
                ] as const
              ).map((s) => (
                <span
                  key={String(s.key)}
                  style={{
                    fontSize: 10,
                    padding: '2px 8px',
                    borderRadius: 20,
                    cursor: 'pointer',
                    border: `0.5px solid ${filters.severity.includes(s.key as 0 | 1 | 2 | null) ? s.bd : 'var(--bd-1)'}`,
                    background: filters.severity.includes(s.key as 0 | 1 | 2 | null) ? s.bg : 'transparent',
                    color: filters.severity.includes(s.key as 0 | 1 | 2 | null) ? s.col : 'var(--t-1)',
                  }}
                  onClick={() => toggleSeverity(s.key)}
                >
                  {s.label}
                </span>
              ))}
            </div>
          </div>

          {sectionLabel('Source')}
          <FilterDropdown
            title="Studio"
            selected={filters.studio}
            options={identityOptions.studios}
            onToggle={(v) => toggleFilterValue('studio', v)}
            onClear={() => clearFilter('studio')}
            placeholder="Search studios…"
          />
          <FilterDropdown
            title="Game"
            selected={filters.game}
            options={identityOptions.games}
            onToggle={(v) => toggleFilterValue('game', v)}
            onClear={() => clearFilter('game')}
            placeholder="Search games…"
          />
          <FilterDropdown
            title="Brand"
            selected={filters.brand}
            options={identityOptions.brands}
            onToggle={(v) => toggleFilterValue('brand', v)}
            onClear={() => clearFilter('brand')}
            placeholder="Search brands…"
          />

          {sectionLabel('Client location')}
          <FilterDropdown
            title="Continent"
            selected={filters.continent}
            options={geoOptions.continents}
            onToggle={(v) => setFilterOnly('continent', v)}
            onClear={() => clearFilter('continent')}
            placeholder="Search continents…"
          />
          <FilterDropdown
            title="Country"
            selected={filters.country}
            options={geoOptions.countries}
            onToggle={(v) => setFilterOnly('country', v)}
            onClear={() => clearFilter('country')}
            placeholder="Search countries…"
          />
          <CityGroupedDropdown
            title="City"
            selected={filters.city}
            citiesByCountry={geoOptions.citiesByCountry}
            cityCountryMap={geoOptions.cityCountryMap}
            onToggleCity={toggleCity}
            onClear={() => clearFilter('city')}
          />

          {sectionLabel('Delivery')}
          <FilterDropdown
            title="CDN edge"
            selected={filters.cdnEdge}
            options={deliveryOptions.cdnEdge}
            onToggle={(v) => toggleFilterValue('cdnEdge', v)}
            onClear={() => clearFilter('cdnEdge')}
            placeholder="Search CDN edges…"
          />
          <SourceUrlAutocomplete
            title="Source URL"
            selected={filters.sourceUrl}
            options={sourceUrlOptions}
            onToggle={(v) => toggleFilterValue('sourceUrl', v)}
            onClear={() => clearFilter('sourceUrl')}
          />
          <FilterDropdown
            title="Stream type"
            selected={filters.streamType}
            options={streamTypeOptions}
            onToggle={(v) => toggleFilterValue('streamType', v)}
            onClear={() => clearFilter('streamType')}
            placeholder="Search types…"
          />

          {sectionLabel('Client device')}
          <FilterDropdown
            title="Device type"
            selected={filters.deviceType}
            options={clientOptions.deviceType}
            onToggle={(v) => toggleFilterValue('deviceType', v)}
            onClear={() => clearFilter('deviceType')}
            placeholder="Search device types…"
          />
          <FilterDropdown
            title="Platform"
            selected={filters.platform}
            options={clientOptions.platform}
            onToggle={(v) => toggleFilterValue('platform', v)}
            onClear={() => clearFilter('platform')}
            placeholder="Search platforms…"
          />
          <FilterDropdown
            title="Browser"
            selected={filters.browser}
            options={clientOptions.browser}
            onToggle={(v) => toggleFilterValue('browser', v)}
            onClear={() => clearFilter('browser')}
            placeholder="Search browsers…"
          />

          <div
            style={{
              gridColumn: '1 / -1',
              borderTop: '0.5px solid var(--bd-0)',
              paddingTop: 10,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 6,
            }}
          >
            <span className="chip" onClick={clearFilters}>
              Clear all
            </span>
            <span
              className="chip on"
              style={{ background: 'var(--info-bg)', color: 'var(--info)', borderColor: 'var(--info-bd)' }}
              onClick={() => setPopOpen(false)}
            >
              Done
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
